import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, KeyRound, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

export default function CustomerLogin() {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [name, setName] = useState("");
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [resto, setResto] = useState(null);
  const { loginWithToken } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const returnTo = loc.state?.from || "/";

  // Extract restaurant slug from the return-to path so we can personalise the hero
  // with this restaurant's logo/name instead of a generic MnuVerse hero.
  const slug = useMemo(() => {
    const m = (returnTo || "").match(/^\/r\/([^/]+)/);
    return m ? m[1] : null;
  }, [returnTo]);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/customer/restaurant/${slug}`);
        if (!cancelled) setResto(data);
      } catch { /* noop — fall back to MnuVerse branding */ }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  const sendOtp = async (e) => {
    e?.preventDefault();
    if (phone.trim().length < 6) { toast.error("Enter a valid phone number"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/send", { phone });
      if (data.existing_user && data.existing_name) {
        setName(data.existing_name);
        toast.success(`Welcome back ${data.existing_name.split(" ")[0]}! OTP: ${data.static_otp || "123456"}`);
      } else {
        toast.success(`Static OTP: ${data.static_otp || "123456"}`);
      }
      setStep(2);
    } catch {
      toast.error("Could not send OTP");
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/otp/verify", { phone, otp, name });
      loginWithToken(data.token, data.user);
      toast.success(`Welcome ${data.user.name}`);
      nav(returnTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid OTP");
    } finally {
      setLoading(false);
    }
  };

  const heroImg = "https://images.unsplash.com/photo-1663530761401-15eefb544889?w=1600&q=80";
  const brandName = resto?.name || "MnuVerse";
  const tagline = resto?.tagline || "Your table. Your pace. Your craving.";
  const accent = resto?.brand_color || "#E5A93C";

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg)", color: "var(--fg)" }}>
      {/* Hero image left */}
      <div className="hidden lg:block relative w-1/2 overflow-hidden">
        <img src={heroImg} className="absolute inset-0 w-full h-full object-cover" alt="" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(10,10,12,0.25), transparent 40%, rgba(10,10,12,0.85))" }} />
        <div className="absolute bottom-10 left-10 right-10 text-white">
          {resto ? (
            <div className="flex items-center gap-3" data-testid="login-resto-brand">
              {resto.logo_url ? (
                <img
                  src={resto.logo_url}
                  alt={resto.name}
                  className="w-14 h-14 rounded-full object-cover border"
                  style={{ borderColor: accent }}
                  data-testid="login-resto-logo"
                />
              ) : (
                <div
                  className="w-14 h-14 rounded-full flex items-center justify-center font-display text-2xl"
                  style={{ background: accent, color: "#0A0A0C" }}
                >
                  {resto.name?.charAt(0) || "M"}
                </div>
              )}
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: accent }}>
                  {resto.cuisine || "Restaurant"}
                </div>
                <div className="font-display text-3xl leading-tight" data-testid="login-resto-name">{resto.name}</div>
              </div>
            </div>
          ) : (
            <Link to="/" className="font-display text-3xl gold-text inline-flex items-center gap-2">
              <Logo size={28} />Mnu<span className="text-white/90">Verse</span>
            </Link>
          )}
          <p className="mt-4 font-display text-4xl leading-tight max-w-md">{tagline}</p>
        </div>
      </div>

      {/* Form right */}
      <div className="flex-1 flex items-center justify-center px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          {/* Mobile-only brand header (hero hidden on small screens) */}
          {resto && (
            <div className="lg:hidden flex items-center gap-3 mb-8" data-testid="login-resto-brand-mobile">
              {resto.logo_url ? (
                <img src={resto.logo_url} alt={resto.name} className="w-12 h-12 rounded-full object-cover border" style={{ borderColor: accent }} />
              ) : (
                <div className="w-12 h-12 rounded-full flex items-center justify-center font-display text-xl" style={{ background: accent, color: "var(--accent-fg)" }}>
                  {resto.name?.charAt(0)}
                </div>
              )}
              <div>
                <div className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: "var(--fg-muted)" }}>{resto.cuisine}</div>
                <div className="font-display text-xl leading-none">{resto.name}</div>
              </div>
            </div>
          )}

          <div className="text-xs font-mono uppercase tracking-[0.2em]" style={{ color: "var(--fg-muted)" }}>Guest Access</div>
          <h1 className="font-display text-5xl mt-2">
            {resto ? `Sign in to ${resto.name.replace(/^The\s+/i, "")}` : "One-tap login"}
          </h1>
          <p className="text-sm mt-2" style={{ color: "var(--fg-muted)" }}>
            {resto ? "Quick OTP login so we can track your table's running order." : "No apps. No passwords. Just your phone."}
          </p>

          {step === 1 ? (
            <form onSubmit={sendOtp} className="mt-8 space-y-4" data-testid="otp-send-form">
              <Field icon={<Phone className="w-4 h-4" />} placeholder="+1 555 000 1111" value={phone} onChange={setPhone} testid="otp-phone-input" />
              <button disabled={loading} data-testid="otp-send-btn" className="gold-btn rounded-full w-full py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? "Sending…" : (<>Send OTP <ArrowRight className="w-4 h-4" /></>)}
              </button>
            </form>
          ) : (
            <form onSubmit={verify} className="mt-8 space-y-4" data-testid="otp-verify-form">
              <div className="text-xs" style={{ color: "var(--fg-muted)" }}>
                Sent to <span className="gold-text">{phone}</span>
              </div>
              <Field placeholder="Your full name" value={name} onChange={setName} testid="otp-name-input" />
              <Field icon={<KeyRound className="w-4 h-4" />} placeholder="123456" value={otp} onChange={setOtp} testid="otp-code-input" />
              <button disabled={loading} data-testid="otp-verify-btn" className="gold-btn rounded-full w-full py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-50">
                {loading ? "Verifying…" : "Enter"}
              </button>
              <button type="button" onClick={() => setStep(1)} className="w-full text-xs" style={{ color: "var(--fg-muted)" }}>Change number</button>
            </form>
          )}
        </motion.div>
      </div>
    </div>
  );
}

function Field({ icon, placeholder, value, onChange, testid }) {
  return (
    <div className="relative">
      {icon && <div className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--fg-dim)" }}>{icon}</div>}
      <input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-xl py-3 focus:ring-1 focus:ring-[var(--accent)] focus:border-[var(--accent)] transition ${icon ? "pl-10 pr-4" : "px-4"}`}
        style={{ background: "var(--bg-card)", border: "1px solid var(--border)", color: "var(--fg)" }}
      />
    </div>
  );
}
